"use client";

import { c as _c } from "react/compiler-runtime";
import { Check, Minus } from "lucide-react";
import Link from "next/link";
import { AuthLink } from "@/components/shared/auth-link";
import { Button } from "@/lib/components/button";
import { cn } from "@/lib/utils";
import { useI18n } from "@llmgateway/i18n/react";
type FeatureValue = boolean | string;
interface PricingFeature {
  nameKey: string;
  descriptionKey?: string;
  learnMoreLink?: string;
  learnMoreKey?: string;
  free: FeatureValue;
  enterprise: FeatureValue;
}
const pricingFeatures: PricingFeature[] = [{
  nameKey: "public.pricing.table.features.platformFees.name",
  free: "public.pricing.table.features.platformFees.free",
  enterprise: "public.pricing.table.features.platformFees.enterprise"
}, {
  nameKey: "public.pricing.table.features.models.name",
  descriptionKey: "public.pricing.table.features.models.description",
  learnMoreLink: "/models",
  learnMoreKey: "public.pricing.table.features.models.learnMore",
  free: "public.pricing.table.features.models.free",
  enterprise: "public.pricing.table.features.models.enterprise"
}, {
  nameKey: "public.pricing.table.features.providerChoice.name",
  descriptionKey: "public.pricing.table.features.providerChoice.description",
  learnMoreLink: "/providers",
  learnMoreKey: "public.pricing.table.features.providerChoice.learnMore",
  free: "public.pricing.table.features.providerChoice.free",
  enterprise: "public.pricing.table.features.providerChoice.enterprise"
}, {
  nameKey: "public.pricing.table.features.freeModels.name",
  descriptionKey: "public.pricing.table.features.freeModels.description",
  free: "public.pricing.table.features.freeModels.free",
  enterprise: "public.pricing.table.features.freeModels.enterprise"
}, {
  nameKey: "public.pricing.table.features.chatAndApi.name",
  descriptionKey: "public.pricing.table.features.chatAndApi.description",
  learnMoreLink: "/guides",
  learnMoreKey: "public.pricing.table.features.chatAndApi.learnMore",
  free: true,
  enterprise: true
}, {
  nameKey: "public.pricing.table.features.activityLogs.name",
  free: true,
  enterprise: true
}, {
  nameKey: "public.pricing.table.features.dataRetention.name",
  free: "public.pricing.table.features.dataRetention.free",
  enterprise: "public.pricing.table.features.dataRetention.enterprise"
}, {
  nameKey: "public.pricing.table.features.autoRouting.name",
  descriptionKey: "public.pricing.table.features.autoRouting.description",
  learnMoreLink: "/features/auto-routing",
  free: true,
  enterprise: true
}, {
  nameKey: "public.pricing.table.features.budgets.name",
  free: true,
  enterprise: true
}, {
  nameKey: "public.pricing.table.features.promptCaching.name",
  descriptionKey: "public.pricing.table.features.promptCaching.description",
  free: true,
  enterprise: true
}, {
  nameKey: "public.pricing.table.features.byok.name",
  descriptionKey: "public.pricing.table.features.byok.description",
  free: "public.pricing.table.features.byok.free",
  enterprise: "public.pricing.table.features.byok.enterprise"
}, {
  nameKey: "public.pricing.table.features.teamManagement.name",
  free: true,
  enterprise: true
}, {
  nameKey: "public.pricing.table.features.advancedAnalytics.name",
  free: true,
  enterprise: true
}, {
  nameKey: "public.pricing.table.features.adminControls.name",
  descriptionKey: "public.pricing.table.features.adminControls.description",
  free: false,
  enterprise: true
}, {
  nameKey: "public.pricing.table.features.dataPolicyRouting.name",
  descriptionKey: "public.pricing.table.features.dataPolicyRouting.description",
  free: false,
  enterprise: true
}, {
  nameKey: "public.pricing.table.features.managedPolicy.name",
  free: false,
  enterprise: true
}, {
  nameKey: "public.pricing.table.features.sso.name",
  free: false,
  enterprise: true
}, {
  nameKey: "public.pricing.table.features.slas.name",
  free: false,
  enterprise: true
}, {
  nameKey: "public.pricing.table.features.whitelabel.name",
  free: false,
  enterprise: true
}, {
  nameKey: "public.pricing.table.features.paymentOptions.name",
  free: "public.pricing.table.features.paymentOptions.free",
  enterprise: "public.pricing.table.features.paymentOptions.enterprise"
}, {
  nameKey: "public.pricing.table.features.rateLimits.name",
  descriptionKey: "public.pricing.table.features.rateLimits.description",
  free: "public.pricing.table.features.rateLimits.free",
  enterprise: "public.pricing.table.features.rateLimits.enterprise"
}, {
  nameKey: "public.pricing.table.features.tokenPricing.name",
  descriptionKey: "public.pricing.table.features.tokenPricing.description",
  learnMoreLink: "/models",
  learnMoreKey: "public.pricing.table.features.tokenPricing.learnMore",
  free: "public.pricing.table.features.tokenPricing.free",
  enterprise: "public.pricing.table.features.tokenPricing.enterprise"
}, {
  nameKey: "public.pricing.table.features.support.name",
  free: "public.pricing.table.features.support.free",
  enterprise: "public.pricing.table.features.support.enterprise"
}];
function FeatureCell(t0) {
  const $ = _c(7);
  const {
    value,
    t
  } = t0;
  if (typeof value === "boolean") {
    let t1;
    if ($[0] !== value) {
      t1 = value ? <Check className="size-5 text-green-500 mx-auto" /> : <Minus className="size-5 text-muted-foreground/50 mx-auto" />;
      $[0] = value;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== t || $[3] !== value) {
    t1 = t(value);
    $[2] = t;
    $[3] = value;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <span className="text-sm text-center block text-muted-foreground">{t1}</span>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
export function PricingTable() {
  const $ = _c(68);
  const {
    t
  } = useI18n();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <th className="text-left p-4 w-1/3" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== t) {
    t1 = t("public.pricing.table.plans.free");
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <div className="font-semibold text-lg text-blue-600 dark:text-blue-400">{t1}</div>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="text-2xl font-bold mt-1">$0</div>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("public.pricing.table.plans.forever");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <div className="text-sm text-muted-foreground">{t4}</div>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t2 || $[11] !== t5) {
    t6 = <th className="p-4 text-center w-1/3 bg-blue-600/10 rounded-t-xl border-x border-t border-blue-600/20">{t2}{t3}{t5}</th>;
    $[10] = t2;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t("public.pricing.table.plans.enterprise");
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t7) {
    t8 = <div className="font-semibold text-lg">{t7}</div>;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t) {
    t9 = t("public.pricing.table.plans.custom");
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t9) {
    t10 = <div className="text-2xl font-bold mt-1">{t9}</div>;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t) {
    t11 = t("public.pricing.table.plans.contactUs");
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11) {
    t12 = <div className="text-sm text-muted-foreground">{t11}</div>;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t10 || $[26] !== t12 || $[27] !== t8) {
    t13 = <th className="p-4 text-center w-1/3">{t8}{t10}{t12}</th>;
    $[25] = t10;
    $[26] = t12;
    $[27] = t8;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t13 || $[30] !== t6) {
    t14 = <thead><tr>{t0}{t6}{t13}</tr></thead>;
    $[29] = t13;
    $[30] = t6;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t) {
    t15 = pricingFeatures.map((feature, index) => <tr key={feature.nameKey} className={cn("border-b border-border/50", index % 2 === 0 ? "bg-muted/30" : "")}><td className="p-4"><div className="font-medium">{t(feature.nameKey)}</div>{feature.descriptionKey && <div className="text-sm text-muted-foreground">{t(feature.descriptionKey)}</div>}{feature.learnMoreLink && feature.learnMoreKey && <Link href={feature.learnMoreLink as any} className="text-xs text-blue-600 hover:underline">{t(feature.learnMoreKey)}</Link>}</td><td className="p-4 text-center bg-blue-600/5 border-x border-blue-600/20"><FeatureCell value={feature.free} t={t} /></td><td className="p-4 text-center"><FeatureCell value={feature.enterprise} t={t} /></td></tr>);
    $[32] = t;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <td className="p-4" />;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t) {
    t17 = t("public.common.getStartedFree");
    $[35] = t;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t17) {
    t18 = <td className="p-6 text-center bg-blue-600/5 border-x border-b border-blue-600/20 rounded-b-xl"><AuthLink href="/signup"><Button className="w-full max-w-[200px]">{t17}</Button></AuthLink></td>;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t) {
    t19 = t("public.pricing.table.cta.contactSales");
    $[39] = t;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t19) {
    t20 = <td className="p-6 text-center"><Link href="/enterprise"><Button variant="outline" className="w-full max-w-[200px]">{t19}</Button></Link></td>;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t18 || $[44] !== t20) {
    t21 = <tr>{t16}{t18}{t20}</tr>;
    $[43] = t18;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t15 || $[47] !== t21) {
    t22 = <tbody>{t15}{t21}</tbody>;
    $[46] = t15;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t14 || $[50] !== t22) {
    t23 = <div className="overflow-x-auto"><table className="w-full border-collapse min-w-[600px]">{t14}{t22}</table></div>;
    $[49] = t14;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t) {
    t24 = t("public.pricing.table.additional.line1");
    $[52] = t;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <br />;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== t) {
    t26 = t("public.pricing.table.additional.prefix");
    $[55] = t;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== t) {
    t27 = t("public.pricing.table.cta.contactSales");
    $[57] = t;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== t27) {
    t28 = <Link href="/enterprise" className="text-blue-600 hover:underline">{t27}</Link>;
    $[59] = t27;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== t24 || $[62] !== t26 || $[63] !== t28) {
    t29 = <div className="mt-12 text-center"><p className="text-muted-foreground">{t24}{t25}{t26}{" "}{t28}.</p></div>;
    $[61] = t24;
    $[62] = t26;
    $[63] = t28;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  if ($[65] !== t23 || $[66] !== t29) {
    t30 = <section className="w-full pb-16 md:pb-24"><div className="container mx-auto px-4 md:px-6">{t23}{t29}</div></section>;
    $[65] = t23;
    $[66] = t29;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  return t30;
}